import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Presentation, Sparkles, Download, Loader2, FileSliders, AlertTriangle, CheckCircle2, TrendingUp, FileSpreadsheet, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { boardPackAPI, branchAPI } from '../../services/api';
import { filterBranchesForUser, getUserBranchScope } from '../../utils/branchScope';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const QUARTERS = [{ v: 1, l: 'Q1 (Apr–Jun)' }, { v: 2, l: 'Q2 (Jul–Sep)' }, { v: 3, l: 'Q3 (Oct–Dec)' }, { v: 4, l: 'Q4 (Jan–Mar)' }];
const HALVES = [{ v: 1, l: 'H1 (Apr–Sep)' }, { v: 2, l: 'H2 (Oct–Mar)' }];
const UPLOADABLE_SECTIONS = ['order_book', 'pnl', 'cash_wc', 'receivables'];

const currentFY = () => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};

const MonthlyReviewCenter = () => {
  const now = new Date();
  const [reviewType, setReviewType] = useState('monthly');
  const [year, setYear] = useState(now.getFullYear());
  const [fyYear, setFyYear] = useState(currentFY());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(1);
  const [half, setHalf] = useState(1);
  const [branch, setBranch] = useState('all');
  const [branches, setBranches] = useState([]);
  const [sectionDefs, setSectionDefs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pack, setPack] = useState(null);
  const [edit, setEdit] = useState({});
  const [overrides, setOverrides] = useState({});
  const [uploadingFor, setUploadingFor] = useState(null);
  const [prevFiles, setPrevFiles] = useState({});
  const fileRefs = useRef({});
  const prevFileRefs = useRef({});

  const unrestricted = !getUserBranchScope();

  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([branchAPI.getAll({ page_size: 200 }), boardPackAPI.sections()]);
        const list = Array.isArray(b) ? b : (b?.items || b?.branches || []);
        setBranches(filterBranchesForUser(list));
        setSectionDefs(s.sections || []);
        setSelected((s.sections || []).map(x => x.id));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load board pack options');
      }
    })();
  }, []);

  const years = useMemo(() => {
    const base = reviewType === 'monthly' ? now.getFullYear() : currentFY();
    return [base, base - 1, base - 2];
  }, [reviewType]); // eslint-disable-line

  const payload = () => ({
    review_type: reviewType,
    year: reviewType === 'monthly' ? year : fyYear,
    month: reviewType === 'monthly' ? month : undefined,
    quarter: reviewType === 'quarterly' ? quarter : undefined,
    half: reviewType === 'half_yearly' ? half : undefined,
    branch: branch === 'all' ? null : branch,
    sections: selected,
  });

  const uploadParams = () => {
    const p = payload();
    return { review_type: p.review_type, year: p.year, month: p.month, quarter: p.quarter, half: p.half, branch: p.branch || '' };
  };

  const fetchOverrides = async () => {
    try {
      const data = await boardPackAPI.getOverrides(uploadParams());
      const map = {};
      (data.uploads || []).forEach(u => { map[u.section] = u; });
      setOverrides(map);
    } catch { /* overrides are optional */ }
  };

  useEffect(() => { fetchOverrides(); }, [reviewType, year, fyYear, month, quarter, half, branch]); // eslint-disable-line

  const handleTemplate = async (section) => {
    try { await boardPackAPI.downloadTemplate(section); } catch { toast.error('Failed to download template'); }
  };

  const handleUpload = async (section, file, previousFile = null) => {
    setUploadingFor(section);
    try {
      const res = await boardPackAPI.uploadOverride({ ...uploadParams(), section }, file, previousFile);
      toast.success(`${res.row_count} rows saved for this period${res.replaced ? ' (replaced previous upload)' : ''}`);
      if (res.reconciliation) {
        const r = res.reconciliation;
        toast.success(`Reconciled: Received ₹${(r.received || 0).toLocaleString('en-IN')} · New invoices ₹${(r.new_invoices || 0).toLocaleString('en-IN')}`);
      }
      if (res.warnings?.length) toast.warning(`${res.warnings.length} row(s) skipped — check the sheet`);
      await fetchOverrides();
      if (pack) toast.info('Regenerate the preview to apply the uploaded data');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed — check the Excel format');
    }
    setUploadingFor(null);
  };

  const handleClearOverride = async (section) => {
    try {
      await boardPackAPI.deleteOverride(overrides[section].id);
      toast.success('Override cleared — ERP data will be used');
      await fetchOverrides();
    } catch { toast.error('Failed to clear upload'); }
  };

  const generate = async () => {
    if (!selected.length) { toast.error('Select at least one report section'); return; }
    setLoading(true);
    try {
      const data = await boardPackAPI.preview(payload());
      setPack(data);
      const e = {
        executive_summary: data.narrative?.executive_summary || '',
        wins: (data.narrative?.wins || []).join('\n'),
        risks: (data.narrative?.risks || []).join('\n'),
        actions: (data.narrative?.actions || []).join('\n'),
      };
      Object.values(data.sections || {}).forEach(s => { e[`sec__${s.id}`] = s.insight || s.verdict || ''; });
      setEdit(e);
      toast.success('Board pack preview ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate preview');
    }
    setLoading(false);
  };

  const download = async () => {
    if (!pack) return;
    setDownloading(true);
    try {
      await boardPackAPI.downloadPptx({ ...payload(), narratives: edit });
      toast.success('PowerPoint downloaded');
    } catch (e) {
      toast.error('Failed to download PowerPoint');
    }
    setDownloading(false);
  };

  const [downloadingRaw, setDownloadingRaw] = useState(false);
  const downloadRaw = async () => {
    if (!selected.length) { toast.error('Select at least one report section'); return; }
    setDownloadingRaw(true);
    try {
      await boardPackAPI.downloadRawData(payload());
      toast.success('Raw data Excel downloaded — one sheet per slide');
    } catch (e) {
      toast.error('Failed to download raw data');
    }
    setDownloadingRaw(false);
  };

  const toggleSection = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selCls = 'w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div data-testid="monthly-review-center">
      <div className="grid grid-cols-1 lg:grid-cols-[330px_1fr] gap-5 items-start">
        {/* LEFT — controls */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4 lg:sticky lg:top-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-blue-700 flex items-center justify-center">
              <Presentation className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Monthly Review Center</h2>
              <p className="text-[10px] text-gray-500">Investor-ready board pack from live ERP data</p>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Review Type</label>
            <div className="grid grid-cols-3 gap-1 mt-1 bg-gray-100 p-1 rounded-lg">
              {[['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['half_yearly', 'Half-Yearly']].map(([v, l]) => (
                <button key={v} data-testid={`review-type-${v}`} onClick={() => setReviewType(v)}
                  className={`px-2 py-1.5 text-[10px] font-semibold rounded-md transition-all ${reviewType === v ? 'bg-white text-blue-800 shadow-sm' : 'text-gray-500'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {reviewType === 'monthly' ? (
              <>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Month</label>
                  <select data-testid="review-month-select" className={selCls} value={month} onChange={e => setMonth(+e.target.value)}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Year</label>
                  <select data-testid="review-year-select" className={selCls} value={year} onChange={e => setYear(+e.target.value)}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{reviewType === 'quarterly' ? 'Quarter (FY)' : 'Half (FY)'}</label>
                  <select data-testid="review-period-select" className={selCls}
                    value={reviewType === 'quarterly' ? quarter : half}
                    onChange={e => reviewType === 'quarterly' ? setQuarter(+e.target.value) : setHalf(+e.target.value)}>
                    {(reviewType === 'quarterly' ? QUARTERS : HALVES).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">FY Starting</label>
                  <select data-testid="review-fy-select" className={selCls} value={fyYear} onChange={e => setFyYear(+e.target.value)}>
                    {years.map(y => <option key={y} value={y}>FY{y}-{String(y + 1).slice(2)}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Branch</label>
            <select data-testid="review-branch-select" className={selCls} value={branch} onChange={e => setBranch(e.target.value)}>
              {unrestricted && <option value="all">All Branches</option>}
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {!unrestricted && <p className="text-[9px] text-gray-400 mt-1">Restricted to your assigned branches</p>}
          </div>

          <div>
            <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Report Sections</label>
            <div className="space-y-1.5 mt-1.5 max-h-64 overflow-y-auto pr-1">
              {sectionDefs.map(s => (
                <label key={s.id} data-testid={`section-card-${s.id}`}
                  className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-all ${selected.includes(s.id) ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="checkbox" className="mt-0.5 accent-blue-700" checked={selected.includes(s.id)} onChange={() => toggleSection(s.id)} />
                  <span>
                    <span className="block text-[11px] font-semibold text-gray-800">{s.title}</span>
                    <span className="block text-[9px] text-gray-500 leading-tight">{s.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {selected.some(id => UPLOADABLE_SECTIONS.includes(id)) && (
            <div data-testid="excel-overrides-block" className="border border-dashed border-blue-300 rounded-lg p-2.5 space-y-2 bg-blue-50/40">
              <p className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> Slide Data via Excel
              </p>
              <p className="text-[9px] text-gray-500 -mt-1 leading-tight">An uploaded sheet replaces ERP/Tally figures for that slide. Saved per period — re-upload to replace and regenerate.</p>
              {UPLOADABLE_SECTIONS.filter(id => selected.includes(id)).map(id => {
                const def = sectionDefs.find(s => s.id === id);
                const ov = overrides[id];
                const rec = ov?.reconciliation;
                return (
                  <div key={id} data-testid={`override-row-${id}`} className="bg-white border border-gray-200 rounded-lg p-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-800">{def?.title || id}</span>
                      {ov
                        ? <span data-testid={`override-status-${id}`} className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Uploaded · {ov.row_count} rows</span>
                        : <span data-testid={`override-status-${id}`} className="text-[9px] font-semibold text-gray-400">ERP data</span>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <button data-testid={`override-template-${id}`} onClick={() => handleTemplate(id)}
                        className="px-2 py-1 text-[9px] font-semibold text-blue-700 border border-blue-200 rounded hover:bg-blue-50">Template</button>
                      <button data-testid={`override-upload-${id}`} onClick={() => fileRefs.current[id]?.click()} disabled={uploadingFor === id}
                        className="px-2 py-1 text-[9px] font-semibold text-white bg-blue-700 rounded hover:bg-blue-800 disabled:opacity-60 flex items-center gap-1">
                        {uploadingFor === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {ov ? 'Re-upload' : 'Upload'}{id === 'receivables' ? ' Current' : ''}
                      </button>
                      {id === 'receivables' && (
                        <button data-testid="override-upload-previous-receivables" onClick={() => prevFileRefs.current[id]?.click()} disabled={uploadingFor === id}
                          className={`px-2 py-1 text-[9px] font-semibold rounded flex items-center gap-1 border ${prevFiles[id] ? 'text-purple-700 border-purple-300 bg-purple-50' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                          <Upload className="w-3 h-3" /> {prevFiles[id] ? 'Prev: ' + prevFiles[id].name.slice(0, 14) : (ov?.previous_filename ? 'Replace Prev' : '+ Previous Month')}
                        </button>
                      )}
                      {ov && (
                        <button data-testid={`override-clear-${id}`} onClick={() => handleClearOverride(id)}
                          className="px-2 py-1 text-[9px] font-semibold text-red-600 border border-red-200 rounded hover:bg-red-50 flex items-center gap-1">
                          <X className="w-3 h-3" /> Clear
                        </button>
                      )}
                      <input type="file" accept=".xlsx" className="hidden" data-testid={`file-input-${id}`}
                        ref={el => { fileRefs.current[id] = el; }}
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(id, f, id === 'receivables' ? (prevFiles[id] || null) : null); if (id === 'receivables') setPrevFiles(p => ({ ...p, [id]: null })); e.target.value = ''; }} />
                      {id === 'receivables' && (
                        <input type="file" accept=".xlsx" className="hidden" data-testid="file-input-previous-receivables"
                          ref={el => { prevFileRefs.current[id] = el; }}
                          onChange={e => { const f = e.target.files?.[0]; if (f) { setPrevFiles(p => ({ ...p, [id]: f })); toast.info('Previous-month file selected — now upload the current-month file'); } e.target.value = ''; }} />
                      )}
                    </div>
                    {id === 'receivables' && (
                      <p className="text-[9px] text-gray-400 mt-1 leading-tight">Your team's format: Party name · Total Due · 30 / 30-60 / 60-90 / 90-180 / 180+ · Payment received this month. Attach the previous-month file too for bucket-wise movement, new invoices & amount received.</p>
                    )}
                    {ov && <p className="text-[9px] text-gray-400 mt-1">{ov.filename} · {ov.uploaded_by} · {String(ov.uploaded_at || '').slice(0, 10)}</p>}
                    {id === 'receivables' && ov?.previous_filename && (
                      <p className="text-[9px] text-purple-500 mt-0.5" data-testid="previous-file-info">Previous: {ov.previous_filename} ({ov.previous_row_count} rows)</p>
                    )}
                    {id === 'receivables' && rec && (
                      <div data-testid="reconciliation-summary" className="mt-1.5 bg-purple-50 border border-purple-200 rounded p-1.5 text-[9px] text-purple-900 space-y-0.5">
                        <p className="font-bold uppercase tracking-wide text-purple-700">Movement vs previous month</p>
                        <p>Opening: ₹{(rec.opening || 0).toLocaleString('en-IN')} → Closing: ₹{(rec.closing || 0).toLocaleString('en-IN')}</p>
                        <p>Received: <strong>₹{(rec.received || 0).toLocaleString('en-IN')}</strong> · New invoices: <strong>₹{(rec.new_invoices || 0).toLocaleString('en-IN')}</strong></p>
                        {(rec.write_offs || 0) > 1 && <p className="text-amber-700">Credit notes / write-offs: ₹{rec.write_offs.toLocaleString('en-IN')}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button data-testid="generate-preview-btn" onClick={generate} disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSliders className="w-4 h-4" />}
            {loading ? 'Preparing…' : 'Generate Preview'}
          </button>

          <button data-testid="download-pptx-btn" onClick={download} disabled={!pack || downloading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? 'Building deck…' : 'Download PowerPoint'}
          </button>

          <button data-testid="download-raw-data-btn" onClick={downloadRaw} disabled={downloadingRaw}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg transition-colors disabled:opacity-50">
            {downloadingRaw ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            {downloadingRaw ? 'Extracting…' : 'Download Raw Data (Excel)'}
          </button>
          <p className="text-[9px] text-gray-400 -mt-2 leading-tight">Raw data: the exact source rows behind every selected slide — audit any number against the modules.</p>
        </div>

        {/* RIGHT — preview */}
        <div className="min-h-[420px]" data-testid="board-pack-preview">
          {!pack && !loading && (
            <div className="flex flex-col items-center justify-center py-24 text-center bg-white border border-dashed border-gray-300 rounded-xl">
              <Presentation className="w-10 h-10 text-gray-300 mb-3" />
              <h3 className="text-sm font-bold text-gray-700">No preview yet</h3>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">Select the review period, branch and report sections, then click <strong>Generate Preview</strong>. Numbers come from live ERP reports; AI writes the board commentary.</p>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-24 bg-white border border-gray-200 rounded-xl">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
              <p className="text-xs text-gray-500">Aggregating reports and writing commentary…</p>
            </div>
          )}

          {pack && !loading && (
            <div className="space-y-4" data-testid="board-pack-preview-loaded">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-blue-800 px-5 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-blue-200">Slide 1 — Title</p>
                      <h3 className="text-lg font-bold mt-0.5">{pack.review_type === 'monthly' ? 'Monthly' : pack.review_type === 'quarterly' ? 'Quarterly' : 'Half-Yearly'} Business Review — {pack.period.label}</h3>
                      <p className="text-xs text-blue-200">{pack.branch_name} • compares {pack.period.prev_label} & {pack.period.ly_label}</p>
                    </div>
                    <span data-testid="ai-badge" className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${pack.ai_generated ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'}`}>
                      <Sparkles className="w-3 h-3" />{pack.ai_generated ? 'AI narrative' : 'Auto narrative'}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Slide 2 — Executive Summary (editable)</p>
                  <textarea data-testid="edit-executive-summary" rows={4} value={edit.executive_summary || ''}
                    onChange={e => setEdit({ ...edit, executive_summary: e.target.value })}
                    className="w-full text-xs text-gray-700 border border-gray-200 rounded-lg p-3 leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {Object.values(pack.sections).map((sec, i) => (
                <div key={sec.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`section-preview-${sec.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-white bg-blue-700 rounded px-1.5 py-0.5">Slide {i + 3}</span>
                    <h4 className="text-sm font-bold text-gray-900">{sec.title}</h4>
                    {sec.source === 'upload' && (
                      <span data-testid={`source-badge-${sec.id}`} className="text-[9px] font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">Uploaded Excel</span>
                    )}
                  </div>
                  {sec.verdict && <p className="text-xs font-semibold text-blue-900 mb-3" data-testid={`verdict-${sec.id}`}>{sec.verdict}</p>}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    {sec.kpis.slice(0, 4).map((k, j) => (
                      <div key={j} className="border-l-2 border-blue-700 bg-gray-50 rounded-r-lg px-3 py-2">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wide">{k.label}</p>
                        <p className="text-sm font-bold text-gray-900">
                          {k.value}
                          {k.delta !== null && k.delta !== undefined && (
                            <span className={`ml-1 text-[9px] font-semibold ${k.delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              {k.delta >= 0 ? '+' : ''}{k.delta}%
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className={`grid grid-cols-1 ${sec.table2 ? 'md:grid-cols-2' : ''} gap-3 mb-3`}>
                    {[sec.table, sec.table2].filter(t => t?.rows?.length > 0).map((t, ti) => (
                      <div key={ti} className="overflow-x-auto">
                        <p className="text-[10px] font-semibold text-gray-500 mb-1">{t.title}</p>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="bg-slate-800 text-white">
                              {t.columns.map(c => <th key={c} className="text-left px-2 py-1.5 font-semibold">{c}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {t.rows.slice(0, 6).map((r, ri) => (
                              <tr key={ri} className={ri % 2 ? 'bg-white' : 'bg-gray-50'}>
                                {r.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-gray-700">{c}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                  {sec.note && <p className="text-[10px] text-amber-600 mb-2">{sec.note}</p>}
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-1">Key insight (editable)</p>
                  <textarea data-testid={`edit-commentary-${sec.id}`} rows={2} value={edit[`sec__${sec.id}`] || ''}
                    onChange={e => setEdit({ ...edit, [`sec__${sec.id}`]: e.target.value })}
                    className="w-full text-xs text-gray-700 border border-gray-200 rounded-lg p-3 leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}

              <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="wins-risks-slide">
                <h4 className="text-sm font-bold text-gray-900 mb-3">Final Slide — Wins, Risks & Actions <span className="text-[9px] font-normal text-gray-400">(one per line, editable)</span></h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <p className="flex items-center gap-1 text-[10px] font-bold text-emerald-700 mb-1"><CheckCircle2 className="w-3 h-3" />WINS</p>
                    <textarea data-testid="edit-wins" rows={5} value={edit.wins || ''} onChange={e => setEdit({ ...edit, wins: e.target.value })}
                      className="w-full text-xs border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <p className="flex items-center gap-1 text-[10px] font-bold text-red-600 mb-1"><AlertTriangle className="w-3 h-3" />RISKS & EXCEPTIONS</p>
                    <textarea data-testid="edit-risks" rows={5} value={edit.risks || ''} onChange={e => setEdit({ ...edit, risks: e.target.value })}
                      className="w-full text-xs border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-red-400" />
                  </div>
                  <div>
                    <p className="flex items-center gap-1 text-[10px] font-bold text-blue-700 mb-1"><TrendingUp className="w-3 h-3" />ACTION POINTS</p>
                    <textarea data-testid="edit-actions" rows={5} value={edit.actions || ''} onChange={e => setEdit({ ...edit, actions: e.target.value })}
                      className="w-full text-xs border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyReviewCenter;
